import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import MarkChatUnreadRounded from "@mui/icons-material/MarkChatUnreadRounded";
import PhoneIphoneRounded from "@mui/icons-material/PhoneIphoneRounded";
import { BadmintonMarkIcon } from "@bw/material";
import { PLATFORM_LEGAL_DISPLAY_NAME } from "@bw/shared";
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const cardSx = {
  p: { xs: 2.25, sm: 3 },
  borderRadius: 3,
  borderColor: "divider",
  bgcolor: "background.paper",
} as const;

/** Public, anonymous carrier-review page for the central BW SMS program. */
export function MessagingProgramPage() {
  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Toolbar>
          <Container maxWidth="lg" disableGutters sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <BadmintonMarkIcon color="primary" aria-hidden="true" />
            <Typography component={RouterLink} to="/" variant="subtitle1" color="text.primary"
              sx={{ fontWeight: 700, textDecoration: "none", flexGrow: 1 }}>
              Badminton Warriors
            </Typography>
            <Button component={RouterLink} to="/login" size="small" variant="outlined">
              Sign in
            </Button>
          </Container>
        </Toolbar>
      </AppBar>

      <Container component="main" maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Stack spacing={{ xs: 4, md: 6 }}>
          <Stack spacing={2} sx={{ maxWidth: 760 }}>
            <Chip label="Transactional account messaging" color="primary" variant="outlined" sx={{ alignSelf: "flex-start" }} />
            <Typography component="h1" variant="h1" sx={{ fontSize: { xs: "2.35rem", md: "3.5rem" }, lineHeight: 1.08 }}>
              Helpful texts from Badminton Warriors—only when you ask for them.
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 680, fontWeight: 400 }}>
              Badminton Warriors is a technology platform for players and independent
              badminton facilities, coaches, court reservations, drop-ins, classes,
              memberships, and events.
            </Typography>
          </Stack>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Paper variant="outlined" component="section" sx={cardSx}>
              <Stack spacing={2}>
                <MarkChatUnreadRounded color="primary" aria-hidden="true" />
                <Typography component="h2" variant="h4">What we send</Typography>
                <Typography color="text.secondary">
                  A one-time verification code when you request one, plus optional
                  service updates about payments, refunds, reservations, drop-in
                  or membership visits, classes, tournaments, and leagues after you
                  separately opt in. You control each service category in your
                  notification settings.
                </Typography>
                <Typography color="text.secondary">
                  Every message identifies <strong>Badminton Warriors</strong> as the
                  sender. A facility or coach name may appear only to explain the
                  activity in your account; independent businesses do not send through
                  this messaging program.
                </Typography>
              </Stack>
            </Paper>

            <Paper variant="outlined" component="section" sx={cardSx}>
              <Stack spacing={2}>
                <PhoneIphoneRounded color="primary" aria-hidden="true" />
                <Typography component="h2" variant="h4">How to opt in</Typography>
                <Stack component="ol" spacing={1.5} sx={{ m: 0, pl: 2.5, color: "text.secondary" }}>
                  <li>Sign in and open <strong>Me → Notification settings</strong>.</li>
                  <li>Request and enter a one-time code to verify your phone.</li>
                  <li>Separately turn on <strong>Transactional text messages</strong>.</li>
                </Stack>
                <Button component={RouterLink} to="/me/notifications" variant="contained" endIcon={<ArrowForwardRounded />} sx={{ alignSelf: "flex-start" }}>
                  Open notification settings
                </Button>
              </Stack>
            </Paper>
          </Box>

          <Paper variant="outlined" component="section" sx={{ ...cardSx, bgcolor: "action.hover" }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
                <CheckCircleRounded color="primary" aria-hidden="true" />
                <Typography component="h2" variant="h4">Your choice stays in your control</Typography>
              </Stack>
              <Typography color="text.secondary">
                Transactional texts are off by default and are never required to make
                a purchase. Message frequency varies with your requests and account
                activity; message and data rates may apply. We do not send marketing
                messages through this program.
              </Typography>
              <Typography color="text.secondary">
                Reply <strong>STOP</strong> to stop texts or turn them off in notification
                settings. Reply <strong>HELP</strong> for assistance, or email{" "}
                <Link href="mailto:support@badmintonwarriors.com">support@badmintonwarriors.com</Link>.
              </Typography>
              <Divider />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 3 }}>
                <Link component={RouterLink} to="/sms-terms">Transactional SMS Terms</Link>
                <Link component={RouterLink} to="/privacy">Privacy Policy</Link>
                <Link component={RouterLink} to="/terms">Terms of Service</Link>
              </Stack>
            </Stack>
          </Paper>

          <Typography variant="body2" color="text.secondary">
            Messaging program operated by {PLATFORM_LEGAL_DISPLAY_NAME}. Carrier
            delivery is not guaranteed; in-app confirmations remain available.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}
